import * as THREE from 'three';
import ObjectArrayView from '../../generic/container/ObjectArrayView';
import ObjectsByName from '../../generic/container/ObjectsByName';
import PolyMesh from '../model/PolyMesh';
import PolyMap from '../model/PolyMap';
import removeDuplicateValues from '../operator/removeDuplicateValues';

export default function Box(width, height, depth, widthSegments, heightSegments, depthSegments) {

  widthSegments = Math.floor(widthSegments) || 1;
  heightSegments = Math.floor(heightSegments) || 1;
  depthSegments = Math.floor(depthSegments) || 1;

  const faceRangeOffsets = new Uint32Array((widthSegments * heightSegments) * 2 + (widthSegments * depthSegments) * 2 + (heightSegments * depthSegments) * 2 + 1);//number of faces
  const faceValueIndices = new Uint32Array(((widthSegments * heightSegments) * 2 + (widthSegments * depthSegments) * 2 + (heightSegments * depthSegments) * 2) * 4);//number of faces*4
  const values = new ObjectArrayView(THREE.Vector3, (widthSegments + 1) * (heightSegments + 1) * 2 + (widthSegments + 1) * (depthSegments + 1) * 2 + (heightSegments + 1) * (depthSegments + 1) * 2);
  const normals = new ObjectArrayView(THREE.Vector3, (widthSegments + 1) * (heightSegments + 1) * 2 + (widthSegments + 1) * (depthSegments + 1) * 2 + (heightSegments + 1) * (depthSegments + 1) * 2);
  const uvValues = new ObjectArrayView(THREE.Vector2, (widthSegments + 1) * (heightSegments + 1) * 2 + (widthSegments + 1) * (depthSegments + 1) * 2 + (heightSegments + 1) * (depthSegments + 1) * 2);

  const segment_width = width / widthSegments;
  const segment_height = height / heightSegments;
  const segment_depth = depth / depthSegments;

  const width_half = width / 2;
  const height_half = height / 2;
  const depth_half = depth / 2;

  let ix, iy, iz;
  let indOffset = 0;

  const newValue = new THREE.Vector3();
  const newNormal = new THREE.Vector3();
  const newUV = new THREE.Vector2();

  //push utility for faceValueIndices
  let indLengthMarker = 0;
  function pushInd(value) {
    faceValueIndices[indLengthMarker] = value;
    indLengthMarker++;
  }

  //populate FaceRangeOffsets
  for (iz = 0, ix = 0; iz < faceRangeOffsets.length; iz++, ix += 4) {
    faceRangeOffsets[iz] = ix;//0 4 8 12 16 20 ...
  }


  //front and back
  for (iz = 0; iz < 2; iz++) {//do twice, one for each face
    for (iy = 0; iy < heightSegments; iy++) {
      for (ix = 0; ix < widthSegments; ix++) {
        pushInd(indOffset + iy * (widthSegments + 1) + ix);
        pushInd(indOffset + iy * (widthSegments + 1) + ix + 1);
        pushInd(indOffset + (iy + 1) * (widthSegments + 1) + ix + 1);
        pushInd(indOffset + (iy + 1) * (widthSegments + 1) + ix);
      }
    }

    indOffset += (widthSegments + 1) * (heightSegments + 1);
  }

  //top and bottom face
  for (iy = 0; iy < 2; iy++) {//do twice, one for each face
    for (ix = 0; ix < widthSegments; ix++) {
      for (iz = 0; iz < depthSegments; iz++) {
        pushInd(indOffset + ix * (depthSegments + 1) + iz);
        pushInd(indOffset + ix * (depthSegments + 1) + iz + 1);
        pushInd(indOffset + (ix + 1) * (depthSegments + 1) + iz + 1);
        pushInd(indOffset + (ix + 1) * (depthSegments + 1) + iz);
      }
    }

    indOffset += (widthSegments + 1) * (depthSegments + 1);
  }

  //left and right face
  for (ix = 0; ix < 2; ix++) {//do twice, one for each face
    for (iz = 0; iz < depthSegments; iz++) {
      for (iy = 0; iy < heightSegments; iy++) {
        pushInd(indOffset + iz * (heightSegments + 1) + iy);
        pushInd(indOffset + iz * (heightSegments + 1) + iy + 1);
        pushInd(indOffset + (iz + 1) * (heightSegments + 1) + iy + 1);
        pushInd(indOffset + (iz + 1) * (heightSegments + 1) + iy);
      }
    }

    indOffset += (depthSegments + 1) * (heightSegments + 1);
  }


  //push utility for faceValueIndices
  let valLengthMarker = 0;
  function pushVal(value) {
    values.setAt(valLengthMarker, value);
    valLengthMarker++;
  }

  //push utility for uv's and normals
  let uv_normLengthMarker = 0;
  function push_uv_norm(uv, norm) {
    uvValues.setAt(uv_normLengthMarker, uv);
    normals.setAt(uv_normLengthMarker, norm);
    uv_normLengthMarker++;
  }

  //populate values, normals, and uv values
  //front and back
  for (iz = -1; iz < 2; iz += 2) {//do twice for each side with -1 for first and +1 for second
    for (iy = 0; iy < heightSegments + 1; iy++) {

      //calculate y coord
      const y = -1 * iz * iy * segment_height + iz * height_half;

      for (ix = 0; ix < widthSegments + 1; ix++) {
        //calculate x coord
        const x = ix * segment_width - width_half;

        //set value
        newValue.set(x, y, -1 * iz * depth_half);
        pushVal(newValue);

        //set normal
        newNormal.set(0, 0, -1 * iz);

        //set uv value
        newUV.set(ix / widthSegments, iy / heightSegments);
        push_uv_norm(newUV, newNormal);

      }
    }
  }

  //top and bottom
  for (iy = -1; iy < 2; iy += 2) {//do twice for each side with -1 for first and +1 for second
    for (ix = 0; ix < widthSegments + 1; ix++) {

      //calculate z coord
      const x = -1 * ix * iy * segment_width + iy * width_half;

      for (iz = 0; iz < depthSegments + 1; iz++) {
        //calculate x coord
        const z = iz * segment_depth - depth_half;

        //set value
        newValue.set(x, -1 * iy * height_half, z);
        pushVal(newValue);

        //set normal
        newNormal.set(0, -1 * iy, 0);

        //set uv value
        newUV.set(ix / widthSegments, iz / depthSegments);
        push_uv_norm(newUV, newNormal);

      }
    }
  }


  //left and right
  for (ix = -1; ix < 2; ix += 2) {//do twice for each side with -1 for first and +1 for second
    for (iz = 0; iz < depthSegments + 1; iz++) {

      //calculate y coord
      const z = -1 * ix * iz * segment_depth + ix * depth_half;

      for (iy = 0; iy < heightSegments + 1; iy++) {
        //calculate z coord
        const y = iy * segment_height - height_half;

        //set value
        newValue.set(-1 * ix * width_half, y, z);
        pushVal(newValue);

        //set normal
        newNormal.set(-1 * ix, 0, 0);

        //set uv value
        newUV.set(iz / depthSegments, iy / heightSegments);
        push_uv_norm(newUV, newNormal);

      }
    }
  }

  const positionsMap = new PolyMap({
    faceRangeOffsets: faceRangeOffsets,
    faceValueIndices: faceValueIndices,
    values: values,
  });

  const normalMap = new PolyMap({
    faceRangeOffsets: faceRangeOffsets,
    faceValueIndices: faceValueIndices,
    values: normals,
  });

  const uvMap = new PolyMap({
    faceRangeOffsets: faceRangeOffsets,
    faceValueIndices: faceValueIndices,
    values: uvValues,
  });

  const uvMapsByName = new ObjectsByName();
  uvMapsByName.set('default', uvMap);

  const polyMesh = new PolyMesh();
  polyMesh.faceRangeOffsets = faceRangeOffsets;
  polyMesh.positions = positionsMap;
  polyMesh.normalMap = normalMap;
  polyMesh.uvMaps = uvMapsByName;

  return removeDuplicateValues(polyMesh);
};



// WEBPACK FOOTER //
// ./src/polyMesh/geometric/primitives/Box.js